import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { HomePage } from "./pages/HomePage";
import { AboutPage } from "./pages/AboutPage";
import { ServicesPage } from "./pages/ServicesPage";
import { ContactPage } from "./pages/ContactPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { CognitiveReachPage } from "./pages/services/CognitiveReachPage";
import { CognitiveBuildPage } from "./pages/services/CognitiveBuildPage";
import { CognitiveIntelligencePage } from "./pages/services/CognitiveIntelligencePage";
import { CognitiveCloudPage } from "./pages/services/CognitiveCloudPage";
import { CognitiveIoTPage } from "./pages/services/CognitiveIoTPage";
import { CognitiveSecurePage } from "./pages/services/CognitiveSecurePage";
import { CognitiveAgentsPage } from "./pages/services/CognitiveAgentsPage";
import { CognitiveGeneratePage } from "./pages/services/CognitiveGeneratePage";
import { CognitiveVisionPage } from "./pages/services/CognitiveVisionPage";
import { CognitiveVoicePage } from "./pages/services/CognitiveVoicePage";
import { CognitivePredictPage } from "./pages/services/CognitivePredictPage";
import { CognitiveCompliancePage } from "./pages/services/CognitiveCompliancePage";
import { CognitiveTrainPage } from "./pages/services/CognitiveTrainPage";
import { CognitiveLedgerPage } from "./pages/services/CognitiveLedgerPage";
import { CognitiveRoboticsPage } from "./pages/services/CognitiveRoboticsPage";
import { HealthcareAIPage } from "./pages/industries/HealthcareAIPage";
import { ManufacturingAIPage } from "./pages/industries/ManufacturingAIPage";
import { AIImplementationGuidePage } from "./pages/blog/AIImplementationGuidePage";
import { CareersPage } from "./pages/CareersPage";
import { InvestorsPage } from "./pages/InvestorsPage";
import { CaseStudiesPage } from "./pages/CaseStudiesPage";
import { BlogPage } from "./pages/BlogPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { FAQPage } from "./pages/FAQPage";
import { PartnershipsPage } from "./pages/PartnershipsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BannerProvider } from "./contexts/BannerContext";
import { OrganizationSchema, LocalBusinessSchema } from "./components/SchemaMarkup";
import { ScrollToTop } from "./components/ScrollToTop";
import { TopBanner } from "./components/TopBanner";

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <BannerProvider>
        <OrganizationSchema />
      <LocalBusinessSchema
        companyName="Cognitive Craft Hub"
        description="Specialized provider of AI-powered business solutions, digital marketing automation, cloud infrastructure, and custom software development services."
        url="https://cognitivecrafthub.com"
        email="info@cognitivecrafthub.com"
        logo="https://cognitivecrafthub.com/images/logo.png"
        socialMedia={[
          "https://www.facebook.com/cognitivecrafthub/",
          "https://www.instagram.com/cognitivecrafthub/",
          "https://x.com/CognitiveCraftH",
          "https://www.linkedin.com/company/cognitivecrafthub"
        ]}
        openingHours={[
          "Mo-Fr 09:00-17:00"
        ]}
      />
      <Router>
        <ScrollToTop />
        <TopBanner />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-100 to-blue-50 text-gray-900 dark:from-gray-900 dark:via-slate-900 dark:to-blue-900 dark:text-white overflow-hidden relative">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 -left-40 w-80 h-80 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-400/10 dark:bg-blue-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-500"></div>
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-blue-600/10 dark:bg-blue-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>
        
        <div className="relative z-10">
          <Header
            isMenuOpen={isMenuOpen}
            setIsMenuOpen={setIsMenuOpen}
          />

          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/services/cognitive-reach" element={<CognitiveReachPage />} />
            <Route path="/services/cognitive-build" element={<CognitiveBuildPage />} />
            <Route path="/services/cognitive-intelligence" element={<CognitiveIntelligencePage />} />
            <Route path="/services/cognitive-cloud" element={<CognitiveCloudPage />} />
            <Route path="/services/cognitive-iot" element={<CognitiveIoTPage />} />
            <Route path="/services/cognitive-secure" element={<CognitiveSecurePage />} />
            <Route path="/services/cognitive-agents" element={<CognitiveAgentsPage />} />
            <Route path="/services/cognitive-generate" element={<CognitiveGeneratePage />} />
            <Route path="/services/cognitive-vision" element={<CognitiveVisionPage />} />
            <Route path="/services/cognitive-voice" element={<CognitiveVoicePage />} />
            <Route path="/services/cognitive-predict" element={<CognitivePredictPage />} />
            <Route path="/services/cognitive-compliance" element={<CognitiveCompliancePage />} />
            <Route path="/services/cognitive-train" element={<CognitiveTrainPage />} />
            <Route path="/services/cognitive-ledger" element={<CognitiveLedgerPage />} />
            <Route path="/services/cognitive-robotics" element={<CognitiveRoboticsPage />} />
            <Route path="/industries/healthcare" element={<HealthcareAIPage />} />
            <Route path="/industries/manufacturing" element={<ManufacturingAIPage />} />
            <Route path="/blog/ai-implementation-guide" element={<AIImplementationGuidePage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/investors" element={<InvestorsPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/success-stories" element={<CaseStudiesPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/partnerships" element={<PartnershipsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>

          <Footer />
        </div>
      </div>
      </Router>
        </BannerProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;